/**
 * V70 엔글 마블 — 리더보드 전체 보기 (Phase 2 FE mock)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeft, Medal, Award } from 'lucide-react';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import { useOrgUnitsPublic } from '../../../shared/hooks/useOrgUnitsPublic';
import { resolveLeaderboardOrgName, buildOrgMap } from '../acidRain/orgLabel';
import { marbleApi } from './mock/serverApi';
import { isPhase3cServerEnabled } from '../shared/phase3cFlag';
import { buildLeaderboardSlots, LEADERBOARD_EMPTY_CELL } from '../shared/leaderboardSlots';
import type { LeaderboardRow } from './engine/types';
// V70-J — 게임대회 공통 헤더
import { TournamentPageHeader, tournamentPageStyle } from '../shared/TournamentPageHeader';
// V70-M — 게임대회 다크 테마 (카드 #111318 · 표 행 구분선 #303540 · 빈 순위 회색)
import { DARK, DARK_EMPTY_CELL, darkSecondaryBtn } from '../shared/tournamentDarkTheme';

export function MarbleLeaderboardPage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { orgUnits } = useOrgUnitsPublic();
  const orgMap = useMemo(() => buildOrgMap(orgUnits || []), [orgUnits]);
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [selfRow, setSelfRow] = useState<LeaderboardRow | null>(null);
  const [loading, setLoading] = useState(true);
  // V70 2026-10-19 — 서버 조회 실패와 기록 없음 구분 (지시 §3 "오류를 빈 기록처럼 표시 금지").
  //   사용자 지시 (보완): 원본 오류 상세는 **사용자 화면 미노출**. boolean 로만 저장 — 상세는 console.warn.
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    (async () => {
      try {
        const lb = await marbleApi.getLeaderboard(user.profile.id);
        if (cancelled) return;
        setRows(lb.rows);
        setSelfRow(lb.self);
        setHasError(false);
      } catch (err) {
        if (cancelled) return;
        console.warn('[MarbleLeaderboard] fetch failed:', err);
        setHasError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    const timer = window.setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      if (!user) return;
      try {
        const lb = await marbleApi.getLeaderboard(user.profile.id);
        if (cancelled) return;
        setRows(lb.rows);
        setSelfRow(lb.self);
        setHasError(false);
      } catch (err) {
        if (cancelled) return;
        console.warn('[MarbleLeaderboard] poll failed:', err);
        setHasError(true);
      }
    }, 61_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [user]);

  return (
    <div style={pageStyle}>
      {/* V70-J — e스포츠 공통 헤더 + 기존 카드 (게임명 제목은 페이지 제목 중복을 피해 h2) */}
      <div style={{ width: '100%', maxWidth: 820, minWidth: 0 }}>
      <TournamentPageHeader page="leaderboard" style={{ marginBottom: 14 }} />
      <div style={cardStyle}>
        {/* QA Issue #1: 서버 모드일 때 데모 배너 → 공식 기록 안내로 교체 (사용자 지시 §3 "로컬 브라우저 범위" 안내 제거) */}
        {/* V70-D — mock 모드 안내 문구 제거 (1~10위 고정 표·"—" 는 유지) */}
        {isPhase3cServerEnabled() && (
          <div style={{ ...demoBanner, background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.4)', color: '#86efac' }}>
            공식 리더보드 — 모든 플레이어의 서버 저장 기록
          </div>
        )}
        <div style={headerRow}>
          <h2 style={titleStyle}>
            <Trophy size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: '#fbbf24' }} />
            엔글 마블 리더보드
          </h2>
          <button style={backBtn} onClick={() => navigate('/tournament/game/marble')}><ArrowLeft size={14} /> 게임 화면</button>
        </div>

        <div style={selfBox}>
          <span style={{ color: DARK.textMuted, fontSize: 12 }}>내 상태</span>
          <div style={{ display: 'flex', gap: 24, marginTop: 6, flexWrap: 'wrap' }}>
            <div><span style={{ color: DARK.textMuted, fontSize: 11 }}>순위</span> <span style={{ color: '#22d3ee', fontWeight: 700, marginLeft: 6 }}>{selfRow?.rank != null ? `${selfRow.rank}위` : '기록 없음'}</span></div>
            <div><span style={{ color: DARK.textMuted, fontSize: 11 }}>누적 점수</span> <span style={{ color: '#fbbf24', fontWeight: 700, marginLeft: 6 }}>{selfRow?.totalScore?.toLocaleString() ?? '-'}</span></div>
            <div><span style={{ color: DARK.textMuted, fontSize: 11 }}>플레이 횟수</span> <span style={{ color: '#f1f5f9', marginLeft: 6 }}>{selfRow?.playCount ?? '-'}</span></div>
          </div>
        </div>

        {loading && <div style={emptyStyle}>로딩 중…</div>}
        {/* V70 2026-10-19 — 서버 조회 실패 시 "기록 없음" 과 구분 (지시 §3)
            사용자 지시: 원본 에러 상세는 화면 미노출. 오류 상세는 console 로만 전달. */}
        {!loading && hasError && (
          <div style={{ ...emptyStyle, color: '#fca5a5', border: '1px solid rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.08)' }}>
            리더보드를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
          </div>
        )}
        {/* V70-C — 게임센터와 같은 정책: 1~10위 행 항상 표시, 유효 기록이 없는 순위는 "—" */}
        {!loading && !hasError && (
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            {/* V70-M — 비율 열 폭 + 말줄임: 모바일에서 이름이 세로로 꺾이지 않게 */}
            <colgroup>
              <col style={{ width: '16%' }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '18%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={thStyle}>순위</th>
                <th style={thStyle}>이름</th>
                <th style={thStyle}>소속</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>누적 점수</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>횟수</th>
              </tr>
            </thead>
            <tbody>
              {buildLeaderboardSlots(rows, (r) => r.uid, (r) => r.totalScore, isPhase3cServerEnabled()).map(({ rank, row: r }) => {
                if (!r) {
                  return (
                    <tr key={`empty-${rank}`} data-testid="lb-empty-row">
                      <td style={{ ...tdStyle, color: DARK_EMPTY_CELL }}>{rank}</td>
                      <td style={{ ...tdStyle, color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                      <td style={{ ...tdStyle, color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                    </tr>
                  );
                }
                const org = resolveLeaderboardOrgName(r, orgMap);
                const toneMap = {
                  sample:     { bg: 'rgba(251, 191, 36, 0.15)', fg: '#fcd34d' },
                  org:        { bg: 'rgba(34, 211, 238, 0.15)', fg: '#67e8f9' },
                  unassigned: { bg: 'rgba(148, 163, 184, 0.15)', fg: '#cbd5e1' },
                  archived:   { bg: 'rgba(248, 113, 113, 0.15)', fg: '#fca5a5' },
                  unknown:    { bg: 'rgba(148, 163, 184, 0.15)', fg: '#94a3b8' },
                } as const;
                const t = toneMap[org.kind];
                return (
                  <tr key={r.uid} data-testid="lb-record-row">
                    <td style={tdStyle}><RankBadge rank={rank} /></td>
                    <td style={{ ...tdStyle, fontWeight: 600 }} title={r.displayName}>{r.displayName}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, background: t.bg, color: t.fg, fontSize: 11, fontWeight: 600, display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                        {org.text}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#fbbf24', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
                      {r.totalScore.toLocaleString()}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: DARK.textMuted }}>{r.playCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#fbbf24', fontWeight: 700 }}><Trophy size={16} /> 1</span>;
  if (rank === 2) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#cbd5e1', fontWeight: 700 }}><Medal size={16} /> 2</span>;
  if (rank === 3) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#f59e0b', fontWeight: 700 }}><Award size={16} /> 3</span>;
  return <span style={{ color: DARK.textMuted, fontWeight: 500 }}>{rank}</span>;
}

// V70-K — 게임대회 전용 네이비 배경으로 통일 (기존 어두운 그라데이션 대체, 카드 내부 배경은 그대로)
const pageStyle: React.CSSProperties = {
  ...tournamentPageStyle({ x: 20, y: 20 }),
  display: 'flex', justifyContent: 'center',
};
const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 820,
  background: DARK.card, border: `1px solid ${DARK.border}`, borderRadius: 16,
  padding: 20, color: DARK.text,
};
const demoBanner: React.CSSProperties = {
  background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)',
  color: '#fbbf24', fontSize: 12, padding: '6px 10px', borderRadius: 6, marginBottom: 12, textAlign: 'center',
};
const headerRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8,
};
const titleStyle: React.CSSProperties = { fontSize: 22, fontWeight: 800, margin: 0, color: DARK.text };
const backBtn: React.CSSProperties = {
  ...darkSecondaryBtn, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
};
const selfBox: React.CSSProperties = {
  background: DARK.row, border: `1px solid ${DARK.border}`, borderRadius: 10,
  padding: 12, marginBottom: 14,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 6px', color: DARK.textMuted, fontSize: 11,
  fontWeight: 500, borderBottom: `1px solid ${DARK.border}`, textTransform: 'uppercase', letterSpacing: 0.5,
  // 머리글은 좁은 화면에서 단어 단위 줄바꿈 (잘림 방지). 데이터 칸은 한 줄 유지
  wordBreak: 'keep-all',
};
const tdStyle: React.CSSProperties = {
  padding: '12px 6px', borderBottom: `1px solid ${DARK.border}`, fontSize: 13, color: DARK.text,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const emptyStyle: React.CSSProperties = { padding: 30, textAlign: 'center', color: DARK.textMuted };

export default MarbleLeaderboardPage;
